import throttle from 'lodash/throttle';
import React, { useRef, useState } from 'react';
import { StyleSheet, View, Text, TextInput as RNTextInput } from 'react-native';

import {
  useConfirmResetPhoneNumberPublicMutation,
  useInitiateResetPhoneNumberPublicMutation,
} from 'apollo/queries/autogenerate/hooks-public';
import { PinCodeDispatchMethod } from 'apollo/queries/autogenerate/schemas';
import { Platform as ReCaptchaPlatform } from 'apollo/queries/autogenerate/schemas-public';
import CodeInput from 'shared/modules/common/components/atoms/CodeInput';
import OTPTimer from 'shared/modules/common/components/organisms/OTPTimer';
import { getParsedPhoneNumber } from 'shared/utils/phone';

type Props = {
  reCaptchaPlatform: ReCaptchaPlatform;
  /**
   *  Every step requires a new ReCaptcha token. This function should return a Promise with the ReCaptcha token as the resolved value.
   */
  getRecaptchaTokenFunction: () => Promise<string>;
  email: string;
  currentPhoneNumber: string;
  newPhoneNumber: string;
  onSuccess: (otp: string) => void;
  onError: (errorType: 'goBackOneStep' | 'sendUserBackToBeginning') => void;
};

const ChangePhoneEnterEmailOTP = (props: Props) => {
  const codeInputRef = useRef<RNTextInput>(null);
  const [emailOTP, setEmailOTP] = useState('');
  const [showError, setShowError] = useState(false);
  const [
    confirmResetPhoneNumberPublic,
  ] = useConfirmResetPhoneNumberPublicMutation({
    context: {
      uri: process.env.CONSUMER_API_HOST_GRAPHQL_PUBLIC,
    },
  });

  const [
    initiateResetPhoneNumberPublic,
  ] = useInitiateResetPhoneNumberPublicMutation({
    context: {
      uri: process.env.CONSUMER_API_HOST_GRAPHQL_PUBLIC,
    },
  });

  // Sends an OTP code to the user's new phone number.
  // Can be invoked to request a new phone number OTP.
  const confirmResetPhoneNumber = async () => {
    setShowError(false);

    try {
      const reCaptchaToken = await props.getRecaptchaTokenFunction();

      await confirmResetPhoneNumberPublic({
        variables: {
          email: props.email,
          currentPhoneNumber: getParsedPhoneNumber(props.currentPhoneNumber),
          newPhoneNumber: getParsedPhoneNumber(props.newPhoneNumber),
          otp: emailOTP,
          pinCodeDispatchMethod: PinCodeDispatchMethod.SMS,
          recaptchaPlatform: props.reCaptchaPlatform,
          recaptchaToken: reCaptchaToken,
        },
      });

      props.onSuccess(emailOTP);
    } catch (err: any) {
      // Handle bad request (400) - send the user back to the beginning
      if (err.graphQLErrors?.[0]?.extensions?.code === 400) {
        console.error(err, '入力内容を確認してください');
        props.onError('sendUserBackToBeginning');

        return;
      }

      setShowError(true);
    }
  };

  // Sends an OTP code to user's email.
  // Can be invoked to request a new email OTP.
  const initiateResetPhoneNumber = throttle(
    async () => {
      setShowError(false);

      try {
        const reCaptchaToken = await props.getRecaptchaTokenFunction();

        await initiateResetPhoneNumberPublic({
          variables: {
            email: props.email,
            currentPhoneNumber: getParsedPhoneNumber(props.currentPhoneNumber),
            recaptchaPlatform: props.reCaptchaPlatform,
            recaptchaToken: reCaptchaToken,
          },
        });

        props.onError('goBackOneStep');
      } catch (err) {
        console.error(err, '入力内容を確認してください');
      }
    },
    2000,
    { leading: true, trailing: false }
  );

  return (
    <View style={style.root}>
      <View style={style.spacer} />
      <Text>
        {props.email}
        に送信された4桁の認証コードを入力してください。
      </Text>
      <View style={style.spacer} />
      <View style={style.otp}>
        <CodeInput
          onFulFill={(code) => {
            setEmailOTP(code);
            confirmResetPhoneNumber();
          }}
          style={style.codeField}
          errorMessage="認証コードに誤りがあります"
          showError={showError}
          id="edit-user-phone-code-field"
          ref={codeInputRef}
        />
      </View>
      <OTPTimer sentToLabel="メール" onPress={initiateResetPhoneNumber} />
    </View>
  );
};

const style = StyleSheet.create({
  root: {
    flexGrow: 1,
  },
  otp: {
    alignItems: 'center',
  },
  codeField: {
    width: 288,
  },
  spacer: {
    marginBottom: 5,
  },
});

export default ChangePhoneEnterEmailOTP;
